// TopicView.jsx — страница темы форума.
// Сверху: backLink + карточка-шапка темы (заголовок, автор, дата, статус).
// Далее: первое сообщение (originalPost) и список комментариев.
// Снизу: пагинация и форма ответа (если тема открыта).

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminService from '../../../API/AdminService';
import CommentForm from './CommentForm';
import cl from './TopicView.module.css';
import logger from '../../../utils/logger';
import {
    IconArrowLeft, IconUser, IconClock, IconEye, IconMessage,
    IconAlert, IconRefresh, IconClose, IconCheck,
} from '../../../components/Icons';

const TopicView = () => {
    const { topicId } = useParams();
    const navigate = useNavigate();
    const [topic, setTopic] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [currentUserId] = useState(null);

    useEffect(() => {
        loadTopicData();
    }, [topicId, currentPage]);

    const loadTopicData = async () => {
        try {
            setLoading(true);
            const response = await AdminService.getForumTopic(topicId, currentPage, 50);
            setTopic(response.topic);
            setComments(response.comments?.data || []);
            setTotalPages(response.comments?.pages || 1);
            setError('');
        } catch (err) {
            setError('Ошибка загрузки темы');
            logger.error('Ошибка загрузки темы:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCommentAdded = () => {
        loadTopicData();
    };

    const handleStatusToggle = async () => {
        if (!topic || topic.author_id !== currentUserId) return;
        try {
            const newStatus = topic.status === 'открыта' ? 'закрыта' : 'открыта';
            await AdminService.updateTopicStatus(topicId, newStatus);
            setTopic(prev => ({ ...prev, status: newStatus }));
        } catch (error) {
            logger.error('Ошибка изменения статуса:', error);
        }
    };

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.loadingContainer}>
                        <div className={cl.spinner}/>
                        <p>Загрузка темы…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.errorState}>
                        <span className={cl.errorIcon}>
                            <IconAlert width={20} height={20}/>
                        </span>
                        <h3>Не удалось загрузить тему</h3>
                        <p>{error}</p>
                        <button onClick={loadTopicData} className={cl.retryButton}>
                            <IconRefresh width={14} height={14}/>
                            <span>Попробовать снова</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!topic) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.errorState}>
                        <span className={cl.errorIcon}>
                            <IconAlert width={20} height={20}/>
                        </span>
                        <h3>Тема не найдена</h3>
                        <button onClick={() => navigate('/main/forum')} className={cl.retryButton}>
                            <IconArrowLeft width={14} height={14}/>
                            <span>Вернуться к форуму</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const isClosed = topic.status === 'закрыта';

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                <button
                    onClick={() => navigate(-1)}
                    className={cl.backLink}
                >
                    <IconArrowLeft width={14} height={14}/>
                    <span>Назад</span>
                </button>

                {/* Шапка темы */}
                <div className={cl.topicHeader}>
                    <h1 className={cl.topicTitle}>{topic.title}</h1>
                    <div className={cl.topicMeta}>
                        <span className={cl.metaItem}>
                            <IconUser width={13} height={13}/>
                            <span>{topic.author_name}</span>
                        </span>
                        <span className={cl.metaItem}>
                            <IconClock width={13} height={13}/>
                            <span>{AdminService.formatForumDate(topic.created_at)}</span>
                        </span>
                        <span className={cl.metaItem}>
                            <IconEye width={13} height={13}/>
                            <span>{topic.views_count}</span>
                        </span>
                        <span className={cl.metaItem}>
                            <IconMessage width={13} height={13}/>
                            <span>{topic.replies_count} {pluralRu(topic.replies_count, 'ответ', 'ответа', 'ответов')}</span>
                        </span>
                        <span className={`${cl.statusPill} ${isClosed ? cl.statusClosed : cl.statusOpen}`}>
                            {isClosed
                                ? <IconClose width={12} height={12}/>
                                : <IconCheck width={12} height={12}/>}
                            <span>{topic.status}</span>
                        </span>
                        {topic.author_id === currentUserId && (
                            <button onClick={handleStatusToggle} className={cl.statusToggle}>
                                {isClosed ? 'Открыть тему' : 'Закрыть тему'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Первое сообщение */}
                <div className={cl.originalPost}>
                    <div className={cl.postAvatar}>
                        {(topic.author_name?.[0] || '?').toUpperCase()}
                    </div>
                    <div className={cl.postBody}>
                        <div className={cl.postHead}>
                            <span className={cl.postAuthor}>{topic.author_name}</span>
                            <span className={cl.postOpBadge}>автор темы</span>
                            <span className={cl.postDate}>
                                {AdminService.formatForumDate(topic.created_at)}
                            </span>
                        </div>
                        <div className={cl.postContent}>
                            {topic.content.split('\n').map((paragraph, index) => (
                                <p key={index}>{paragraph}</p>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Комментарии */}
                <div className={cl.commentsSection}>
                    <div className={cl.commentsHead}>
                        <h2 className={cl.commentsTitle}>
                            Ответы ({topic.replies_count})
                        </h2>
                    </div>

                    {comments.length === 0 ? (
                        <div className={cl.noComments}>
                            Пока нет ответов. Будьте первым!
                        </div>
                    ) : (
                        <div className={cl.commentsList}>
                            {comments.map(comment => (
                                <CommentItem key={comment.id} comment={comment} />
                            ))}
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className={cl.pagination}>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`${cl.pageButton} ${page === currentPage ? cl.activePage : ''}`}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>
                    )}

                    {!isClosed && (
                        <div className={cl.commentFormWrap}>
                            <CommentForm
                                topicId={topicId}
                                onCommentAdded={handleCommentAdded}
                            />
                        </div>
                    )}
                    {isClosed && (
                        <div className={cl.closedNotice}>
                            <IconAlert width={14} height={14}/>
                            <span>Тема закрыта — новые ответы добавить нельзя.</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


// ----- Один комментарий -----
const CommentItem = ({ comment }) => {
    const initial = (comment.author_name?.[0] || '?').toUpperCase();
    return (
        <div className={cl.commentItem}>
            <div className={cl.commentAvatar}>{initial}</div>
            <div className={cl.commentBody}>
                <div className={cl.commentHead}>
                    <span className={cl.commentAuthor}>{comment.author_name}</span>
                    <span className={cl.commentDate}>
                        {AdminService.formatForumDate(comment.created_at)}
                    </span>
                </div>
                <div className={cl.commentContent}>
                    {comment.content.split('\n').map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                    ))}
                </div>
            </div>
        </div>
    );
};

function pluralRu(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
}

export default TopicView;
